import WorkStart from "../../components/WorkStart";
import { customerWorkActions } from "../../lib/simpleNavigation";
import { websiteReachabilityVerified } from "../../lib/environmentEvidence";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Inbox, CheckSquare, MessageCircle, TrendingUp, ChevronRight, Circle, Clock, ShieldCheck, ReceiptText, Users, AppWindow, ExternalLink, Globe, Wrench, FileText, Database, BarChart3, AlertTriangle, FolderOpen, Bell, Download, Send, X, Loader2, ArrowUpRight, ArrowRight, Plus, Lock, Activity, Zap, Calendar, Smartphone, Headphones, Sparkles, Star, LayoutTemplate, WifiOff } from "lucide-react";
import api from "@/lib/api";
import { useCockpit } from "./CockpitLayout";
import { Card, KpiTile, Sparkline, BtnPrimary, fmt, SectionLabel, Badge, HealthRing, WebsitePreview, healthScore, healthColor, healthBand } from "./ui";
import { EditPencil, EditDialog } from "./CockpitEdit";
import { chf } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import GettingStarted from "./GettingStarted";
import { buildChecklist } from "./checklist";
import FirstRecommendation from "./FirstRecommendation";
import CustomerCareCard from "./CustomerCareCard";

const CONTACT_CATEGORIES = ["support", "anfrage", "rechnung", "webseite", "aufgabe", "sonstiges"];
const CONTACT_CAT_LABEL = { support: "Support", anfrage: "Anfrage", rechnung: "Rechnung", webseite: "Website", aufgabe: "Aufgabe", sonstiges: "Sonstiges" };
const NOTIF_LABEL = { task: "Neue Aufgabe", support: "Neue Antwort im Support", invoice: "Neue Rechnung", document: "Neues Dokument", report: "Monatsreport bereit", reminder: "Freigabe wartet auf Sie" };
const NOTIF_ICON = { task: CheckSquare, support: MessageCircle, invoice: ReceiptText, document: FolderOpen, report: FileText, reminder: Clock };
const NOTIF_TONE = { task: "#F59E0B", support: "#3B82F6", invoice: "#8B5CF6", document: "#A3E635", report: "#22C55E", reminder: "#F59E0B" };
const DOC_CAT_LABEL = { vertrag: "Vertrag", briefing: "Briefing", rechnung: "Rechnung", bericht: "Bericht", bild: "Bild", sonstiges: "Sonstiges" };
const PRIO_TONE = { hoch: "red", mittel: "amber", niedrig: "slate" };
const PRIO_LABEL = { hoch: "Hoch", mittel: "Mittel", niedrig: "Niedrig" };

const PwaBanner = ({ accent, t, nav, base }) => {
  const [installEvt, setInstallEvt] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("kewag_pwa_banner_dismissed") === "1");
  const isStandalone = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator?.standalone === true);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  if (isStandalone || dismissed) return null;
  const dismiss = () => { localStorage.setItem("kewag_pwa_banner_dismissed", "1"); setDismissed(true); };
  const go = async () => {
    if (installEvt) { installEvt.prompt(); try { await installEvt.userChoice; } catch {} setInstallEvt(null); dismiss(); }
    else nav(`${base}/security`);
  };
  return (
    <div data-testid="ov-pwa-banner" className="relative flex flex-wrap sm:flex-nowrap items-center gap-3 rounded-2xl border px-4 py-3 pr-12 sm:pr-4"
      style={{ borderColor: `${accent}33`, background: `${accent}0D` }}>
      <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1A`, color: accent }}><Smartphone className="w-5 h-5" /></span>
      <div className="min-w-0 flex-1 basis-[calc(100%-3rem)] sm:basis-auto">
        <div className="text-[13px] font-bold">{t("KEWAGpage als App nutzen")}</div>
        <div className="text-xs text-gray-400 truncate">{t("Zum Home-Bildschirm hinzufügen — schneller Zugriff und später Push-Benachrichtigungen.")}</div>
      </div>
      <button onClick={go} data-testid="ov-pwa-install"
        className="shrink-0 w-full sm:w-auto justify-center inline-flex items-center gap-1.5 font-bold py-2 px-4 rounded-xl text-[13px] hover:opacity-90 transition-opacity" style={{ background: accent, color: "#071005" }}>
        <Download className="w-4 h-4" /> {installEvt ? t("App installieren") : t("Anleitung anzeigen")}
      </button>
      <button onClick={dismiss} data-testid="ov-pwa-dismiss" aria-label={t("Ausblenden")}
        className="shrink-0 absolute top-3 right-3 sm:static w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors"><X className="w-4 h-4" /></button>
    </div>
  );
};

export default function Overview() {
  const { overview, navigation = [], accent, base, clientId, openAi } = useCockpit();
  const canOpen = key => navigation.some(it => it.key === key);
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [contact, setContact] = useState(null); // null | {category, subject}
  const [nextAction, setNextAction] = useState(null);
  useEffect(() => {
    let active = true;
    setNextAction(null);
    api.get(`/cockpit/next-action${clientId ? `?client_id=${encodeURIComponent(clientId)}` : ""}`)
      .then((r) => { if (active) setNextAction(r.data.action); }).catch(() => {});
    return () => { active = false; };
  }, [clientId]);
  const {
    client, website, package: pkg, subscription, latest_report, open_leads, open_tasks, urgent_tasks, pending_tasks,
    open_support, open_invoices, visits, delta_pct, recent_changes, next_steps_preview, included_features,
    active_modules, billing, care_team, documents_count, recent_invoices, recent_tickets, recent_documents,
    recent_notifications, visits_series, current_work,
  } = overview;

  const online = websiteReachabilityVerified(website);
  const analyticsConnected = !!website.analytics_connected;
  const openInvAmount = (recent_invoices || []).filter((i) => i.status !== "bezahlt").reduce((s, i) => s + (i.gross_chf || 0), 0);
  const sinceDate = fmtDate(client.contract_start) || fmtDate(website.last_checked);
  const firstName = client.contact_person?.split(" ")[0] || client.company_name;
  const clientStatus = { aktiv: "Aktiv", in_vorbereitung: "In Vorbereitung", pausiert: "Pausiert", gekuendigt: "Gekündigt", archiviert: "Archiviert" }[client.status] || "Nicht verifiziert";
  const health = healthScore(website?.states);
  const openContact = (preset) => setContact(preset || { category: "support", subject: "" });

  const { user } = useAuth();
  const canReadTasks = user.role !== "client_staff" || (user.allowed_sections || []).includes("tasks");
  const [gsDismissed, setGsDismissed] = useState(() => localStorage.getItem("kewag_checklist_dismissed") === "1");
  const [gsCelebrated] = useState(() => localStorage.getItem("kewag_checklist_celebrated") === "1");
  const checklistItems = buildChecklist({ user, overview });
  const gsAllDone = checklistItems.every((i) => i.done);
  const showChecklist = !clientId && ["client", "client_staff"].includes(user.role) && !gsDismissed
    && (checklistItems.some((i) => !i.done) || (gsAllDone && !gsCelebrated));
  const recLoader = React.useCallback(
    (skipList = []) => api.get(`/cockpit/ai/first-recommendation?lang=${lang}${clientId ? `&client_id=${clientId}` : ""}${skipList.length ? `&skip=${skipList.join(",")}` : ""}`).then((r) => r.data),
    [lang, clientId]
  );

  return (
    <div className="fade-in space-y-5">
      {/* ===== Begrüssung ===== */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[26px] lg:text-[34px] leading-tight font-black tracking-tight flex items-center gap-2.5" data-testid="ov-greeting">
            {t("Guten Tag")}, {firstName}! <span className="inline-block">👋</span>
          </h1>
          <p className="text-sm text-gray-400 mt-1.5">{t("Wir kümmern uns um Ihre Website, Aufgaben und Betreuung.")}</p>
        </div>
        <div className="flex items-center gap-2 text-xs font-semibold px-3.5 py-2 rounded-full border self-start" data-testid="ov-status-badge"
          style={client.status === "aktiv" ? { background: `${accent}14`, color: accent, borderColor: `${accent}33` }
            : { background: "#ffffff08", color: "#d1d5db", borderColor: "#ffffff26" }}>
          {t("Kundenstatus:")} {t(clientStatus)}
        </div>
      </div>

      {/* ===== Nächste Aktion (die eine dringendste offene Freigabe) ===== */}
      {nextAction && <NextActionCard action={nextAction} accent={accent} t={t} onGo={() => nav(`${base}/${nextAction.link}`)} />}

      {/* ===== PWA-Hinweis (dezent, nur wenn App nicht installiert) ===== */}
      {!clientId && ["client", "client_staff"].includes(user.role) && <PwaBanner accent={accent} t={t} nav={nav} base={base} />}

      {/* ===== Erste Schritte (echt, selbstvervollstaendigend) ===== */}
      {showChecklist && (
        <GettingStarted items={checklistItems} accent={accent}
          onGo={(to) => nav(`${base}/${to}`)}
          onCelebrated={() => localStorage.setItem("kewag_checklist_celebrated", "1")}
          onDismiss={() => { localStorage.setItem("kewag_checklist_dismissed", "1"); setGsDismissed(true); }} />
      )}

      <WorkStart customer actions={customerWorkActions(navigation)} accent={accent} />

      <div className="grid gap-5 items-stretch" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))" }} data-testid="ov-daily-focus">
        {canOpen("website") && <HeuteWichtigCard canRequest={canOpen("support")} website={website} online={online} nextStep={next_steps_preview?.[0]} accent={accent} t={t}
          onVisit={() => website.public_url && window.open(website.public_url, "_blank", "noopener,noreferrer")}
          onRequest={() => openContact({ category: "webseite", subject: t("Inhalte anfordern") })}
          onChange={() => openContact({ category: "webseite", subject: t("Änderung melden") })} />}
        {canOpen("support") && <CustomerCareCard care_team={care_team} accent={accent} t={t} onMessage={() => openContact({ category: "support", subject: "" })} />}
      </div>

      <details open data-testid="cockpit-overview-details" className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 sm:p-5">
        <summary className="cursor-pointer min-h-[44px] text-sm font-bold text-gray-200 focus-visible:outline focus-visible:outline-2">{t("Weitere Angaben und Verlauf")}</summary>
        <div className="space-y-5 pt-5">
      {/* ===== KPI-Strip: nur freigegebene Bereiche ===== */}
      <div className="grid gap-3 lg:gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 140px), 1fr))" }} data-testid="ov-kpis">
        {canOpen("website") && <KpiTile primary icon={Globe} tone={online ? "green" : "gray"} accent={accent} value={online ? t("Erreichbar") : t("Nicht aktuell geprüft")} label={t("Website")} sub={sinceDate ? `${t("Seit")} ${sinceDate}` : t("In Betreuung")} testid="ov-kpi-website" onClick={() => nav(`${base}/website`)} />}
        {canReadTasks && <KpiTile icon={CheckSquare} tone="amber" accent={accent} value={open_tasks} label={t("Offene Aufgaben")} sub={urgent_tasks ? `${urgent_tasks} ${t("überfällig")}` : (pending_tasks ? t("1 zur Freigabe") : t("alles im Griff"))} testid="ov-tasks" onClick={() => nav(`${base}/tasks`)} />}
        {canOpen("support") && <KpiTile icon={MessageCircle} tone="blue" accent={accent} value={open_support} label={t("Support")} sub={t("Offene Tickets")} testid="ov-support" onClick={() => nav(`${base}/support`)} />}
        {canOpen("invoices") && <KpiTile icon={ReceiptText} tone="purple" accent={accent} value={open_invoices} label={t("Rechnungen")} sub={open_invoices ? `${chf(openInvAmount)} ${t("offen")}` : t("alles bezahlt")} testid="ov-kpi-invoices" onClick={() => nav(`${base}/invoices`)} />}
        {canOpen("documents") && <KpiTile icon={FolderOpen} tone="lime" accent={accent} value={documents_count || 0} label={t("Dokumente")} sub={t("verfügbar")} testid="ov-kpi-documents" onClick={() => nav(`${base}/documents`)} />}
        {canOpen("performance") && <KpiTile icon={TrendingUp} tone="green" accent={accent} value={analyticsConnected ? fmt(visits) : "—"} label={t("Besuche")} trend={analyticsConnected ? `+${delta_pct}%` : null} sub={analyticsConnected ? t("Letzte 30 Tage") : t("Nicht verbunden")} testid="ov-visits" onClick={() => nav(`${base}/performance`)} />}
      </div>

      {/* ===== KI-Empfehlung: grösster Hebel diesen Monat (echt, GPT-4o) ===== */}
      {canOpen("next-steps") && <FirstRecommendation load={recLoader} accent={accent} onGo={(to) => nav(`${base}/${to}`)} onAskAi={() => openAi && openAi()} />}

      {/* ===== Relevante Arbeit und freigegebene Bereiche ===== */}
      <div className="flex flex-col xl:flex-row gap-5">
        <div className="flex-1 grid gap-5 items-stretch" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))" }}>
          {canReadTasks && <KewagWorkingCard changes={recent_changes} work={current_work} nextStep={next_steps_preview?.[0]} accent={accent} t={t} lang={lang} onAll={() => nav(`${base}/tasks`)} />}
          {canOpen("invoices") && <PaketCard pkg={pkg} subscription={subscription} billing={billing} included_features={included_features} active_modules={active_modules} accent={accent} t={t} chf={chf} onManage={() => nav(`${base}/invoices`)} />}
        </div>
      </div>

      {/* ===== Nächste Schritte · Letzte Änderungen · Betreuungsteam · Hilfe ===== */}
      <div className="grid gap-5 items-start" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))" }}>
        {canOpen("next-steps") && <NextStepsCard items={next_steps_preview} accent={accent} t={t} nav={nav} base={base} />}
        <ActivityCard items={recent_changes} accent={accent} t={t} lang={lang} />
        {canOpen("support") && <HelpCard accent={accent} t={t} onAi={() => openAi && openAi()} onHuman={() => openContact({ category: "support", subject: "" })} />}
      </div>

      {/* ===== Team-Sicherheit (nur Kunden-Admin) ===== */}
      <TeamSecurityCard accent={accent} clientId={clientId} base={base} t={t} nav={nav} />

      {/* ===== Support · Rechnungen · Dokumente · Benachrichtigungen ===== */}
      <div className="grid gap-5 items-start" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))" }}>
        {canOpen("support") && <SupportListCard items={recent_tickets} accent={accent} t={t} lang={lang} onAll={() => nav(`${base}/support`)} onNew={() => openContact({ category: "support", subject: "" })} />}
        {canOpen("invoices") && <InvoiceListCard items={recent_invoices} subscription={subscription} accent={accent} t={t} chf={chf} onAll={() => nav(`${base}/invoices`)} />}
        {canOpen("documents") && <DocsListCard items={recent_documents} count={documents_count} accent={accent} t={t} onAll={() => nav(`${base}/documents`)} />}
        <NotifCard items={recent_notifications} accent={accent} t={t} nav={nav} lang={lang} />
      </div>

      {client.special_app_name && <SpecialAppCard client={client} accent={accent} t={t} />}

      </div>
      </details>
      {contact && <ContactModal t={t} accent={accent} clientId={clientId} preset={contact} onClose={() => setContact(null)} />}
    </div>
  );
}

/* ============================ Nächste Aktion ============================ */
const NA_ICON = { task: CheckSquare, design: LayoutTemplate, review: Star };
const NextActionCard = ({ action, accent, t, onGo }) => {
  const Ic = NA_ICON[action.type] || Sparkles;
  const more = (action.count || 1) - 1;
  const days = action.pending_days;
  return (
    <div data-testid="ov-next-action" className="relative overflow-hidden rounded-2xl border p-5 flex flex-col sm:flex-row sm:items-center gap-4"
      style={{ borderColor: `${accent}44`, background: `linear-gradient(100deg, ${accent}1F 0%, ${accent}0A 45%, rgba(10,10,12,0) 78%)` }}>
      <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${accent}22`, color: accent }}><Ic className="w-6 h-6" /></span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-[0.15em] font-bold flex items-center gap-2 flex-wrap" style={{ color: accent }}>
          {t("Ihre nächste Aktion")}
          {more > 0 && <span className="normal-case tracking-normal text-gray-400 font-semibold">· +{more} {t("weitere")}</span>}
          {typeof days === "number" && days >= 3 && (
            <span data-testid="ov-next-action-days" className="normal-case tracking-normal inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: "rgba(245,158,11,0.16)", color: "#fbbf24" }}>
              <Clock className="w-3 h-3" /> {t("Seit {n} Tagen offen").replace("{n}", String(days))}
            </span>
          )}
        </div>
        <div className="text-lg font-black text-white truncate mt-0.5" data-testid="ov-next-action-title">{action.title}</div>
        <div className="text-sm text-gray-300 line-clamp-2 sm:truncate">{action.description}</div>
      </div>
      <button onClick={onGo} data-testid="ov-next-action-cta"
        className="shrink-0 inline-flex items-center justify-center gap-2 font-bold py-2.5 px-5 rounded-xl text-sm hover:opacity-90 transition-opacity w-full sm:w-auto" style={{ background: accent, color: "#071005" }}>
        {t(action.cta_label)} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
};

/* ============================ Heute wichtig ============================ */
const HeuteWichtigCard = ({ website, online, nextStep, accent, t, onVisit, onRequest, onChange, canRequest = true }) => {
  const w = website || {};
  const hasUrl = !!w.public_url;
  const initial = (w.domain || "K").replace(/^www\./, "").charAt(0).toUpperCase();
  return (
    <Card className="p-5 flex flex-col" data-testid="ov-heute-wichtig">
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className="text-base font-bold text-white">{t("Heute wichtig")}</span>
        <Badge tone={online ? "green" : "amber"} accent={accent}>{online ? t("Erreichbarkeit geprüft") : t("Nicht aktuell geprüft")}</Badge>
      </div>
      <WebsitePreview url={hasUrl ? w.public_url : null} domain={hasUrl ? w.domain : null} initial={initial} accent={accent} t={t} live={online} maxWidthClass="max-w-full" />
      <div className="mt-4 space-y-2 flex-1">
        <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: online ? accent : "#fbbf24" }}>
          <CheckCircle2 className="w-4 h-4 shrink-0" /> {online ? t("Website erreichbar") : t("Aktueller Status offen")}
        </div>
        <p className="text-sm text-gray-400">{online ? t("Bei der letzten Prüfung erreichbar.") : t("Eine aktuelle Prüfung ist noch offen.")}</p>
        <div className="text-sm">
          <span className="text-gray-500">{t("Nächster Schritt:")}</span>{" "}
          <span className="text-gray-200">{nextStep?.title || t("Kein nächster Schritt hinterlegt.")}</span>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button onClick={onVisit} disabled={!hasUrl} data-testid="ov-hw-visit"
          className={`inline-flex items-center gap-1.5 font-bold py-2 px-4 rounded-xl text-sm hover:opacity-90 transition-opacity ${!hasUrl ? "opacity-50 cursor-not-allowed" : ""}`} style={{ background: accent, color: "#071005" }}>
          {t("Zur Website")} <ArrowRight className="w-3.5 h-3.5" />
        </button>
        {canRequest && <button onClick={onRequest} data-testid="ov-hw-request" className="inline-flex items-center gap-1.5 font-semibold py-2 px-3 rounded-xl text-sm bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 transition-colors">
          <FileText className="w-3.5 h-3.5" /> {t("Inhalte anfordern")}
        </button>}
        {canRequest && <button onClick={onChange} data-testid="ov-hw-change" className="inline-flex items-center gap-1.5 font-semibold py-2 px-3 rounded-xl text-sm bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 transition-colors">
          <MessageCircle className="w-3.5 h-3.5" /> {t("Änderung melden")}
        </button>}
      </div>
    </Card>
  );
};

/* ============================ KEWAG arbeitet gerade ============================ */
const KewagWorkingCard = ({ changes, work, nextStep, accent, t, lang, onAll }) => {
  const lastChange = (changes || []).find((c) => c.state === "done" && c.at) || (changes || []).find((c) => c.state === "done");
  const active = (work || [])[0];
  return (
    <Card className="p-5 flex flex-col" data-testid="ov-kewag-working">
      <span className="text-base font-bold text-white mb-3">{t("KEWAG arbeitet gerade")}</span>
      <div className="space-y-3 flex-1">
        <WorkRow icon={CheckCircle2} tone={accent} label={t("Letzte Änderung")}
          title={lastChange?.text || t("Noch keine Änderung erfasst")} meta={lastChange?.at ? timeAgo(lastChange.at, lang) : null} />
        <WorkRow icon={Loader2} tone="#fbbf24" spin label={t("Aktuelle Arbeiten")}
          title={active?.title || t("Aktuell keine offenen Arbeiten")} meta={active ? t(active.status_label || "In Arbeit") : null} metaTone="#fbbf24" />
        <WorkRow icon={Calendar} tone="#60a5fa" label={t("Nächster geplanter Schritt")}
          title={nextStep?.title || t("Kein geplanter Schritt")} meta={nextStep?.due_date ? `${t("Geplant für")} ${fmtDate(nextStep.due_date)}` : null} />
      </div>
      <button onClick={onAll} data-testid="ov-working-all" className="mt-4 w-full flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 transition-colors">
        {t("Alle Arbeiten ansehen")} <ChevronRight className="w-4 h-4" />
      </button>
    </Card>
  );
};

const WorkRow = ({ icon: Icon, tone, spin, label, title, meta, metaTone }) => (
  <div className="flex items-start gap-3 py-1">
    <span className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${tone}1A`, color: tone }}>
      <Icon className={`w-4 h-4 ${spin ? "animate-spin" : ""}`} />
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
      <div className="text-sm font-semibold text-gray-100 truncate">{title}</div>
      {meta && <div className="text-[11px] mt-0.5" style={{ color: metaTone || "#6b7280" }}>{meta}</div>}
    </div>
  </div>
);

/* ============================ Hilfe-Karte ============================ */
const HelpCard = ({ accent, t, onAi, onHuman }) => (
  <Card className="p-5 flex flex-col" data-testid="ov-help-card">
    <span className="text-base font-bold text-white">{t("Wie können wir helfen?")}</span>
    <p className="text-sm text-gray-400 mt-1 mb-4">{t("Schnell, einfach und persönlich.")}</p>
    <div className="space-y-3 flex-1">
      <button onClick={onAi} data-testid="ov-help-ai" className="w-full flex items-center gap-3 p-3 rounded-xl text-left border transition-colors hover:opacity-90"
        style={{ background: `${accent}0D`, borderColor: `${accent}33` }}>
        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1F`, color: accent }}><Sparkles className="w-4 h-4" /></span>
        <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-gray-100">{t("Frage stellen")}</div><div className="text-[11px] text-gray-500">{t("Persönlich & direkt")}</div></div>
        <ArrowRight className="w-4 h-4 shrink-0" style={{ color: accent }} />
      </button>
      <button onClick={onHuman} data-testid="ov-help-human" className="w-full flex items-center gap-3 p-3 rounded-xl text-left bg-white/[0.02] border border-white/10 hover:bg-white/[0.05] transition-colors">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-white/5 text-gray-300"><Headphones className="w-4 h-4" /></span>
        <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-gray-100">{t("Mit Mensch sprechen")}</div><div className="text-[11px] text-gray-500">{t("Persönlich & direkt")}</div></div>
        <ArrowRight className="w-4 h-4 text-gray-500 shrink-0" />
      </button>
    </div>
  </Card>
);

/* ============================ Team-Sicherheit (2FA-Ampel) ============================ */
const TeamSecurityCard = ({ accent, clientId, base, t, nav }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const cid = clientId || user?.client_id;
  useEffect(() => {
    if (user?.role !== "client" || !cid) return;
    api.get(`/clients/${cid}/users`)
      .then((r) => { const list = r.data || []; setStats({ total: list.length, active: list.filter((u) => u.totp_enabled).length }); })
      .catch(() => setStats(false));
  }, [cid, user?.role]);

  if (user?.role !== "client" || !stats) return null;
  const { total, active } = stats;
  const allGood = total > 0 && active === total;
  const none = active === 0;
  const tone = allGood ? accent : none ? "#f87171" : "#fbbf24";
  const missing = total - active;
  const pct = total ? Math.round((active / total) * 100) : 0;

  return (
    <Card className="p-5" data-testid="ov-team-security">
      <div className="flex items-center gap-4 flex-wrap">
        <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${tone}1A`, color: tone }}>
          <ShieldCheck className="w-6 h-6" />
        </span>
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold">{t("Team-Sicherheit")}</span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${tone}1A`, color: tone }}>
              {allGood ? t("Optimal") : none ? t("Handlungsbedarf") : t("Fast dort")}
            </span>
          </div>
          <p className="text-sm text-gray-300 mt-0.5" data-testid="ov-team-security-text">
            {t("{a} von {b} im Team haben die Zwei-Faktor-Anmeldung aktiv.").replace("{a}", String(active)).replace("{b}", String(total))}
          </p>
          <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden max-w-xs">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: tone }} />
          </div>
        </div>
        <button onClick={() => nav(`${base}/team`)} data-testid="ov-team-security-btn"
          className="shrink-0 flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl border transition-colors hover:opacity-90"
          style={{ borderColor: `${tone}44`, background: `${tone}10`, color: tone }}>
          {missing > 0 ? t("{n} ohne 2FA — jetzt erinnern").replace("{n}", String(missing)) : t("Team ansehen")}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </Card>
  );
};

/* ============================ Nächste Schritte ============================ */
const NextStepsCard = ({ items, accent, t, nav, base }) => (
  <Card className="p-5 h-full flex flex-col" data-testid="ov-next-steps">
    <CardHead title={t("Nächste Schritte")} action={t("Alle anzeigen")} onAction={() => nav(`${base}/next-steps`)} accent={accent} />
    <div className="space-y-1 flex-1">
      {(items || []).slice(0, 4).map((s) => (
        <button key={s.id} onClick={() => nav(`${base}/next-steps`)} data-testid={`ov-step-${s.id}`} className="w-full flex items-center gap-3 py-2.5 text-left hover:opacity-90 border-b border-white/5 last:border-0">
          <span className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}14`, color: accent }}><ArrowUpRight className="w-4 h-4" /></span>
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate text-gray-200">{s.title}</div>
            {s.due_date && <div className="text-[11px] text-gray-500">{t("Fällig")}: {fmtDate(s.due_date)}</div>}
          </div>
          {s.priority && <Badge tone={PRIO_TONE[s.priority] || "slate"} accent={accent}>{t(PRIO_LABEL[s.priority] || s.priority)}</Badge>}
          <ChevronRight className="w-4 h-4 text-gray-600 shrink-0" />
        </button>
      ))}
      {(!items || items.length === 0) && <div className="flex flex-col items-center justify-center py-8 text-center"><CheckCircle2 className="w-8 h-8 mb-2" style={{ color: accent }} /><p className="text-sm text-gray-400">{t("Keine offenen Punkte.")}</p></div>}
    </div>
  </Card>
);

/* ============================ Paket ============================ */
export const PaketCard = ({ pkg, subscription, billing, included_features, active_modules, accent, t, chf, onManage }) => {
  const { canEdit, clientId, refresh } = useCockpit();
  const [edit, setEdit] = useState(false);
  const [packages, setPackages] = useState([]);
  useEffect(() => { if (edit && packages.length === 0) api.get("/packages").then((r) => setPackages(r.data || [])).catch(() => {}); }, [edit, packages.length]);
  const savePkg = async (form) => { await api.put(`/clients/${clientId}`, { package_id: form.package_id }); await refresh(); };
  return (
  <Card className="p-5 flex flex-col relative overflow-hidden" data-testid="ov-package">
    <div className="absolute top-0 inset-x-0 h-1" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}33)` }} />
    {canEdit && <EditPencil onClick={() => setEdit(true)} testid="edit-paket" accent={accent} />}
    <div className="flex items-center justify-between gap-2">
      <SectionLabel>{t("Ihr Paket")}</SectionLabel>
      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${accent}1A`, color: accent }}>{t("Aktiv")}</span>
    </div>
    <div className="flex items-end justify-between gap-2 mt-1">
      <div className="text-2xl font-black tracking-tight">{pkg?.name}</div>
      <div className="text-right" data-testid="ov-package-price"><span className="text-xl font-black" style={{ color: accent }}>{chf(billing?.total_monthly ?? billing?.package_price ?? subscription?.price_chf)}</span><span className="text-xs text-gray-500"> {t("/ Monat")}</span></div>
    </div>
    <div className="text-[11px] text-gray-500 mt-0.5">{t("inkl. MWST")} · {t("Laufzeit bis")} {subscription?.renewal_date || "—"}</div>
    {(included_features || []).length > 0 && (
      <div className="mt-3 space-y-1.5" data-testid="ov-package-features">
        {included_features.slice(0, 4).map((feat, i) => (<div key={i} className="flex items-center gap-1.5 text-xs text-gray-300"><CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} /> {feat}</div>))}
      </div>
    )}
    {(active_modules || []).length > 0 && (
      <div className="mt-3 pt-3 border-t border-white/5">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">{t("Aktive Zusatzmodule")}</div>
        <div className="space-y-1.5" data-testid="ov-active-modules">
          {active_modules.map((m, i) => (
            <div key={i} className="flex items-center justify-between gap-2 text-xs">
              <span className="flex items-center gap-1.5 text-gray-300 min-w-0"><CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} /> <span className="truncate">{m.name}</span></span>
              {(m.price_chf || 0) > 0 && billing?.has_addons ? <span className="font-semibold shrink-0" style={{ color: accent }}>{chf(m.price_chf)}</span> : <span className="text-gray-500 shrink-0">{t("Inklusive")}</span>}
            </div>
          ))}
        </div>
      </div>
    )}
    {billing?.has_addons && (
      <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between" data-testid="ov-total-monthly">
        <span className="text-sm font-semibold">{t("Total monatlich")}</span>
        <span className="text-sm font-black" style={{ color: accent }}>{chf(billing.total_monthly)}</span>
      </div>
    )}
    <button onClick={onManage} data-testid="ov-manage-package" className="mt-4 w-full flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 transition-colors">{t("Paket & Leistungen ansehen")} <ChevronRight className="w-4 h-4" /></button>
    {canEdit && <EditDialog open={edit} onClose={() => setEdit(false)} title="Paket ändern" accent={accent} testid="edit-paket-dialog"
      onSave={savePkg} initial={{ package_id: pkg?.id || "" }}
      fields={[{ key: "package_id", label: "Paket", type: "select", options: packages.map((p) => ({ value: p.id, label: p.name })), hint: "Preis & enthaltene Leistungen folgen automatisch dem gewählten Paket." }]} />}
  </Card>
  );
};

/* ============================ Benachrichtigungen ============================ */
const NotifCard = ({ items, accent, t, nav, lang }) => (
  <Card className="p-5" data-testid="ov-notif-list">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2"><Bell className="w-5 h-5" style={{ color: accent }} /><span className="font-bold">{t("Benachrichtigungen")}</span></div>
    </div>
    <div className="space-y-0.5">
      {(items || []).map((n) => {
        const Ic = NOTIF_ICON[n.type] || Bell; const tn = NOTIF_TONE[n.type] || accent;
        return (
          <button key={n.id} onClick={() => n.link && nav(n.link)} data-testid={`ov-notif-${n.id}`} className="w-full flex items-center gap-3 py-2.5 text-left hover:opacity-90 border-b border-white/5 last:border-0">
            <span className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${tn}1F`, color: tn }}><Ic className="w-4 h-4" /></span>
            <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate text-gray-200">{t(NOTIF_LABEL[n.type] || "Benachrichtigung")}</div><div className="text-[11px] text-gray-500 truncate">{n.ref} · {timeAgo(n.created_at, lang)}</div></div>
            {!n.read && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: accent }} />}
          </button>
        );
      })}
      {(!items || items.length === 0) && <p className="text-sm text-gray-500 py-3 text-center">{t("Keine neuen Benachrichtigungen.")}</p>}
    </div>
  </Card>
);

/* ============================ Support-Liste ============================ */
const SupportListCard = ({ className = "", items, accent, t, lang, onAll, onNew }) => (
  <Card className={`p-5 flex flex-col ${className}`} data-testid="ov-support-list">
    <CardHead title={t("Support-Tickets")} action={t("Alle anzeigen")} onAction={onAll} accent={accent} />
    <div className="space-y-2 flex-1">
      {(items || []).map((tk) => (
        <button key={tk.id} onClick={onAll} data-testid={`ov-ticket-${tk.id}`} className="w-full flex items-start gap-3 p-3 rounded-xl text-left bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 transition-colors">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${accent}14`, color: accent }}><MessageCircle className="w-4 h-4" /></span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2"><span className="text-sm font-semibold truncate text-gray-100 flex-1">{tk.subject}</span><Badge tone={tk.can_confirm ? "green" : tk.needs_reply ? "amber" : tk.open ? "blue" : "slate"} accent={accent}>{t(tk.status_label)}</Badge></div>
            <div className="text-[11px] text-gray-500 mt-0.5">{tk.ticket_no} · {timeAgo(tk.last_at, lang)}</div>
          </div>
        </button>
      ))}
      {(!items || items.length === 0) && <div className="flex flex-col items-center justify-center py-8 text-center"><MessageCircle className="w-8 h-8 mb-2 text-gray-600" /><p className="text-sm text-gray-400">{t("Keine Supportfälle.")}</p></div>}
    </div>
    <BtnPrimary accent={accent} className="w-full mt-3" data-testid="ov-new-ticket" onClick={onNew}><Plus className="w-4 h-4" /> {t("Neues Ticket erstellen")}</BtnPrimary>
  </Card>
);

/* ============================ Rechnungen ============================ */
const InvoiceListCard = ({ className = "", items, subscription, accent, t, chf, onAll }) => (
  <Card className={`p-5 flex flex-col ${className}`} data-testid="ov-invoice-list">
    <CardHead title={t("Rechnungen")} action={t("Alle anzeigen")} onAction={onAll} accent={accent} />
    <div className="space-y-2 flex-1">
      {(items || []).map((inv) => (
        <button key={inv.id} onClick={onAll} data-testid={`ov-inv-${inv.id}`} className="w-full flex items-center gap-3 p-3 rounded-xl text-left bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 transition-colors">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#8B5CF61F", color: "#A78BFA" }}><ReceiptText className="w-4 h-4" /></span>
          <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate text-gray-100">{inv.number || inv.period}</div><div className="text-[11px] text-gray-500 truncate">{inv.service_period || fmtDate(inv.issue_date)}</div></div>
          <div className="text-right shrink-0"><div className="text-sm font-black">{chf(inv.gross_chf)}</div><Badge tone={inv.status === "bezahlt" ? "green" : inv.status === "überfällig" ? "red" : "amber"} accent={accent}>{t(inv.status_label)}</Badge></div>
        </button>
      ))}
      {(!items || items.length === 0) && <div className="flex flex-col items-center justify-center py-8 text-center"><ReceiptText className="w-8 h-8 mb-2 text-gray-600" /><p className="text-sm text-gray-400">{t("Noch keine Rechnungen.")}</p></div>}
    </div>
    <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-white/5">{t("Vertragslaufzeit bis:")} <span className="text-gray-300 font-semibold">{subscription?.renewal_date || "—"}</span></p>
  </Card>
);

/* ============================ Dokumente ============================ */
const DocsListCard = ({ className = "", items, count, accent, t, onAll }) => (
  <Card className={`p-5 flex flex-col ${className}`} data-testid="ov-docs-list">
    <CardHead title={t("Dokumente")} action={t("Alle anzeigen")} onAction={onAll} accent={accent} />
    <div className="space-y-1 flex-1">
      {(items || []).map((d) => (
        <button key={d.id} onClick={onAll} data-testid={`ov-doc-${d.id}`} className="w-full flex items-center gap-3 py-2.5 text-left hover:opacity-90 border-b border-white/5 last:border-0">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}14`, color: accent }}><FileText className="w-4 h-4" /></span>
          <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate text-gray-100">{d.title}</div><div className="text-[11px] text-gray-500 truncate">{t(DOC_CAT_LABEL[d.category] || "Sonstiges")} · {fileSize(d.file_size)}</div></div>
          <Download className="w-4 h-4 text-gray-500 shrink-0" />
        </button>
      ))}
      {(!items || items.length === 0) && (
        <div className="flex flex-col items-center justify-center py-8 text-center" data-testid="ov-docs-empty">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ background: `${accent}14`, color: accent }}><FolderOpen className="w-6 h-6" /></div>
          <p className="text-sm font-semibold text-gray-300">{t("Noch keine Dokumente vorhanden.")}</p>
          <p className="text-xs text-gray-500 mt-1 max-w-[230px]">{t("Dokumente, Verträge und Dateien werden hier für Sie bereitgestellt.")}</p>
        </div>
      )}
    </div>
    {count > 3 && <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-white/5 text-center">{t("Insgesamt")} {count} {t("Dokumente")}</p>}
  </Card>
);

/* ============================ Aktivitäts-Timeline ============================ */
const ActivityCard = ({ items, accent, t, lang }) => (
  <Card className="p-5 h-full" data-testid="ov-activity">
    <div className="flex items-center gap-2 mb-3"><Activity className="w-5 h-5" style={{ color: accent }} /><span className="font-bold">{t("Letzte Änderungen")}</span></div>
    <div className="space-y-0.5">
      {(items || []).slice(0, 7).map((ch, i) => (
        <div key={i} className="flex items-center gap-2.5 py-2 border-b border-white/5 last:border-0" data-testid={`ov-change-${i}`}>
          {ch.state === "done" ? <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: accent }} /> : <Circle className="w-2.5 h-2.5 shrink-0 fill-current text-amber-400 mx-[3px]" />}
          <span className={`text-sm flex-1 min-w-0 truncate ${ch.state === "done" ? "text-gray-300" : "text-amber-300/90"}`}>{ch.text}</span>
          {ch.at && <span className="text-[11px] text-gray-500 shrink-0">{timeAgo(ch.at, lang)}</span>}
        </div>
      ))}
      {(!items || items.length === 0) && <p className="text-sm text-gray-500 py-3 text-center">{t("Noch keine Aktivität.")}</p>}
    </div>
  </Card>
);

/* ============================ Spezial-App ============================ */
const SpecialAppCard = ({ client, accent, t }) => {
  const appLive = ["online", "aktiv"].includes(client.special_app_status);
  return (
    <Card className="p-5" data-testid="cockpit-special-app">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2"><AppWindow className="w-5 h-5" style={{ color: accent }} /><span className="font-bold">{client.special_app_name}</span></div>
        <Badge tone={SA_TONE[client.special_app_status] || "amber"} accent={accent}>{t(STATUS_LABEL[client.special_app_status] || "In Vorbereitung")}</Badge>
      </div>
      <div className="space-y-2.5 text-sm my-4">
        <Row icon={Globe} label={t("Website")} value={client.domain} accent={accent} />
        <Row icon={AppWindow} label={t("App-Domain")} value={client.app_domain || "—"} accent={accent} />
      </div>
      {appLive && client.special_app_url ? (
        <BtnPrimary accent={accent} className="w-full" data-testid="cockpit-open-special-app" onClick={() => window.open(client.special_app_url, "_blank", "noopener,noreferrer")}><ExternalLink className="w-4 h-4" /> {client.special_app_name} {t("öffnen")}</BtnPrimary>
      ) : (
        <div className="w-full rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 flex items-center justify-center gap-2 text-sm text-amber-300" data-testid="cockpit-app-not-active"><AlertTriangle className="w-4 h-4" /> {t("App-Domain noch nicht aktiv")}</div>
      )}
    </Card>
  );
};

/* ============================ Monatsreport (in CWebsite verwendet) ============================ */
const Metric = ({ label, value, trend, series, color }) => (
  <div className="flex-1 min-w-0">
    <div className="text-[11px] text-gray-500 truncate">{label}</div>
    <div className="flex items-baseline gap-1.5"><span className="text-xl font-black tracking-tight">{value}</span>{trend != null && <span className="text-[11px] font-bold text-green-400">+{trend}%</span>}</div>
    <div className="mt-1 h-[34px]"><Sparkline data={series} color={color} height={34} /></div>
  </div>
);

export const MonatsreportCard = ({ report, visits, delta, series, leads, connected, accent, t, onView }) => {
  const pv = report?.page_views ?? null;
  const d = delta || 0;
  const tp = connected ? (report?.top_pages || [])[0] : null;
  const anfragen = fmt(report?.leads_count ?? leads ?? 0);
  return (
    <Card className="p-5 flex flex-col" data-testid="ov-monthly-report">
      <CardHead title={`${t("Monatsreport")}${report?.month_label ? " · " + report.month_label : ""}`} action={t("Report ansehen")} onAction={onView} accent={accent} />
      {connected ? (
        <div className="flex gap-4 lg:gap-5">
          <Metric label={t("Besucher")} value={fmt(visits)} trend={d || null} series={series || []} color={accent} />
          <Metric label={t("Seitenaufrufe")} value={pv != null ? fmt(pv) : "—"} trend={null} series={[]} color="#3B82F6" />
          <Metric label={t("Anfragen")} value={anfragen} trend={null} series={[]} color="#8B5CF6" />
        </div>
      ) : (
        <div className="flex gap-6">
          <div className="flex-1"><div className="text-[11px] text-gray-500">{t("Besucher")}</div><div className="text-xl font-black tracking-tight text-gray-600">—</div></div>
          <div className="flex-1"><div className="text-[11px] text-gray-500">{t("Neue Anfragen")}</div><div className="text-xl font-black tracking-tight">{anfragen}</div></div>
        </div>
      )}
      {report?.summary && <p className="text-[12px] text-gray-300 mt-3 leading-snug flex-1">{report.summary}</p>}
      {tp && (
        <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between text-[12px]">
          <span className="text-gray-500">{t("Top Seite")}: <span className="text-gray-200 font-medium">{tp.page}</span></span>
          <span className="font-bold" style={{ color: accent }}>{fmt(tp.views)} {t("Besuche")}</span>
        </div>
      )}
      {!connected && <p className="text-[11px] text-gray-500 mt-3 flex items-center gap-1.5"><WifiOff className="w-3.5 h-3.5 text-gray-500" /> {t("Noch nicht verbunden — echte Besuchszahlen erscheinen, sobald Ihre Website-Analyse aktiv ist.")}</p>}
    </Card>
  );
};

/* ============================ Kleinteile ============================ */
const CardHead = ({ title, action, onAction, accent }) => (
  <div className="flex items-center justify-between mb-3">
    <span className="text-base font-bold text-white">{title}</span>
    {action && <button onClick={onAction} className="text-xs font-semibold hover:opacity-80 flex items-center gap-1" style={{ color: accent }}>{action} <ChevronRight className="w-3.5 h-3.5" /></button>}
  </div>
);

function ContactModal({ t, accent, clientId, preset, onClose }) {
  const [form, setForm] = useState({ subject: preset?.subject || "", message: "", category: preset?.category || "support" });
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const qs = clientId ? `?client_id=${clientId}` : "";
  const submit = async () => {
    if (!form.subject.trim() || !form.message.trim()) return toast.error(t("Bitte Betreff und Nachricht ausfüllen."));
    setBusy(true); setFailed(false);
    try {
      const { data } = await api.post(`/cockpit/support${qs}`, form);
      toast.success(t("Nachricht gesendet · Support-Ticket erstellt") + (data?.ticket_no ? ` (${data.ticket_no})` : ""));
      onClose();
    } catch (e) { setFailed(true); toast.error(e.response?.data?.detail || t("Senden fehlgeschlagen. Bitte nutzen Sie den E-Mail-Link.")); }
    finally { setBusy(false); }
  };
  const inp = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-current";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ov-contact-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#16161c] border border-white/10 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10"><h3 className="font-bold">{t("Nachricht an KEWAG senden")}</h3><button onClick={onClose} className="p-1 text-gray-500 hover:text-white"><X className="w-5 h-5" /></button></div>
        <div className="p-6 space-y-3">
          <p className="text-xs text-gray-500">{t("Ihre Nachricht wird als Support-Ticket an Ihr KEWAG-Team übermittelt.")}</p>
          <div><label className="text-xs text-gray-400 mb-1 block">{t("Kategorie")}</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="ov-contact-category" className={inp} style={{ color: "#fff" }}>
              {CONTACT_CATEGORIES.map((c) => <option key={c} value={c} style={{ color: "#000" }}>{t(CONTACT_CAT_LABEL[c])}</option>)}
            </select>
          </div>
          <div><label className="text-xs text-gray-400 mb-1 block">{t("Betreff")}</label><input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="ov-contact-subject" className={inp} placeholder={t("Worum geht es?")} /></div>
          <div><label className="text-xs text-gray-400 mb-1 block">{t("Nachricht")}</label><textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ov-contact-message" rows={4} className={inp} placeholder={t("Beschreiben Sie Ihr Anliegen...")} /></div>
          {failed && <a href="mailto:support@kewagpage.ch" data-testid="ov-contact-mailto" className="block text-center text-xs font-semibold py-2 rounded-lg border border-amber-400/40 text-amber-300">{t("KEWAG Support per E-Mail kontaktieren")}</a>}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
            <button onClick={submit} disabled={busy} data-testid="ov-contact-submit" className="font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50 text-black" style={{ background: accent }}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("Senden")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_LABEL = { online: "Online", in_pruefung: "In Vorbereitung", offline: "Offline", aktiv: "Online", in_vorbereitung: "In Vorbereitung", pausiert: "In Vorbereitung" };
const SA_TONE = { online: "green", aktiv: "green", in_pruefung: "amber", in_vorbereitung: "amber", pausiert: "amber", offline: "red" };

const Row = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-gray-400"><Icon className="w-4 h-4 text-gray-500" /> {label}</span><span className="font-semibold" style={{ color: accent }}>{value}</span></div>
);

const fmtDate = (s) => { try { return s ? new Date(s).toLocaleDateString("de-CH") : ""; } catch { return ""; } };
const fmtDateTime = (s) => { try { return s ? new Date(s).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" }) : ""; } catch { return ""; } };
const fileSize = (b) => { b = b || 0; if (b < 1024) return `${b} B`; if (b < 1048576) return `${Math.round(b / 1024)} KB`; return `${(b / 1048576).toFixed(1)} MB`; };
const timeAgo = (s, lang) => {
  if (!s) return "";
  try {
    const diff = (new Date(s).getTime() - Date.now()) / 1000; const abs = Math.abs(diff);
    const rtf = new Intl.RelativeTimeFormat(lang || "de", { numeric: "auto" });
    if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
    if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
    return rtf.format(Math.round(diff / 86400), "day");
  } catch { return ""; }
};

